import { useEffect, useMemo } from "react";
import {
  getConnectionSuggestion,
  getIsLoadingConnectionSuggestionsList,
  loadConnectionSuggestions,
  useAppDispatch,
  useAppSelector,
} from "@lib/store";
import { useTranslation } from "react-i18next";
import { ListHeader } from "../ListHeader";
import { Card } from "@atoms";
import { cn } from "@utils";
import { ConnectionSuggestionItem } from "./ConnectionSuggestionItem";
import { ConnectionSuggestionLoaderItem } from "./ConnectionSuggestionLoaderItem";
import { useBreakPoint } from "@hooks";

type Props = {
  className?: string;
};

export const ConnectionSuggestionSection = ({ className }: Props) => {
  const { t } = useTranslation();
  const { suggestions: connectionSuggestions, ...feedbackData } = useAppSelector(getConnectionSuggestion);
  const dispatch = useAppDispatch();
  const isLoadingConnectionsSuggestions = useAppSelector(getIsLoadingConnectionSuggestionsList);

  const hasConnectionSuggestions = useMemo(() => {
    return connectionSuggestions && connectionSuggestions.length > 0;
  }, [connectionSuggestions]);

  const breakPoint = useBreakPoint();

  useEffect(() => {
    dispatch(loadConnectionSuggestions());
  }, [dispatch]);

  return (
    <Card className={cn("flex flex-col xs:p-6 px-4 sm:px-6 md:px-8 py-8 gap-6", className)}>
      <ListHeader title={t("connection:PeopleYouMayKnow")} />

      <div
        className={cn("grid divide-y ", {
          "[&>*:nth-child(n+3)]:hidden": breakPoint === "medium" || breakPoint === "small",
        })}>
        {isLoadingConnectionsSuggestions ? (
          Array.from({ length: 4 }).map((_, index) => <ConnectionSuggestionLoaderItem key={index} />)
        ) : hasConnectionSuggestions ? (
          connectionSuggestions.map(connectionSuggestion => (
            <ConnectionSuggestionItem
              key={connectionSuggestion.connectedUser.id}
              connectionSuggestion={connectionSuggestion}
              feedbackData={{ ...feedbackData, suggestionId: feedbackData.id }}
            />
          ))
        ) : (
          <p data-testid="connection-suggestions-empty">{t("connection:EmptySuggestions")}</p>
        )}
      </div>
    </Card>
  );
};
